import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import { isUndef } from '@/lib/utils';
import TeamApply from '@/models/teamApply';

export interface TeamInfoData {
    id: number;
    show: boolean
    has_full: boolean
    funds: number
    level_name: string
    reward: {
        [price: string]: [number, number]
    }

}
export interface MonitorState {
    activity: {
        [mode: string]: ListWrap<Monitor.Activity> & ListReq;
    };
    /** 战队列表 */
    teamListMap: {
        [activityId: string]: Monitor.Team[] | undefined,
    };
    /** 战队信息 */
    teamDataMap: {
        [teamId: string]: Monitor.TeamData | undefined,
    };
    /** 战队信息 */
    teamInfoMap: {
        [teamId: string]: TeamApply | undefined,
    };
    /** 招募榜单 */
    recruitRank: {
        [activityId: string]: ListWrap<Monitor.RecruitRankItem> & ListUser<Monitor.RecruitRankItem | null> & ListRequesting | undefined,
    };
    /** 影响数据 */
    affectStat: {
        [key: string]: {
            joined: number,
            not_joined: number,
            list: Monitor.AffectItem[],
        },
    };
    /** 影响人数列表 */
    affectList: {
        [key: string]: ListWrap<Monitor.AffectItem> & ListRequesting | undefined,
    };
    /** 战队奖励 */
    activityIdMapPrizeList: {
        [id: string]: Activity.Prize[] | undefined,
    };
    /** 战队每天的打卡概况 */
    teamSignInMap: {
        [teamId: string]: {
            [date: string]: Monitor.TeamSignIn | undefined,
        } | undefined,
    };
    /** 战队每天的打卡详情 所属哪个战队 哪天 哪种类型 */
    teamSignInDetailMap: {
        [teamId: string]: {
            [date: string]: {
                [type: string]: Monitor.TeamSignInDetail[] | undefined;
            } | undefined,
        } | undefined,
    };
    /** 战队中某个人打卡日历 */
    userSignInCalendarMap: {
        [userId: string]: {
            [month: string]: Monitor.CalendarMonth | undefined,
        } | undefined,
    };
    /** 某个人打卡日历的固定数据 key=activityId + '_' + userId */
    userSignInStatic: {
        [key: string]: {
            nickname: string;
            avatar: string;
            total_days: number;
            total_distance: number;
            future_day: number;
            need_day: number;
        } | undefined,
    };
    /** 活动最新战报信息 */
    inviteNews: {
        [activityId: string]: Monitor.New[] | undefined,
    };
    newsRequesing: boolean;
    /** 邀请成为队长邀请卡 */
    monitorInviteCard: {
        [activityId: string]: string,
    };
    /** 推荐战队 */
    recommendTeamList: {
        order_team: Monitor.Team[],
        recommend_team: Monitor.Team[],
    };
    /** 笔试题 */
    monitorQuestion: Monitor.MonitorQuestion[] | null;
    /** 笔试题分数 */
    monitorQuestionScore: number | null;
    /** 申请的队伍 */
    teamApply: TeamApply | null;
    /** 上一次创建队伍信息 */
    lastTeamInfo: TeamApply | null;
    teamVisiableMap: {
        [teamId: number]: boolean | undefined;
    };
    teamInfo: null | TeamInfoData
}

function initRecruitRank(map: any, activityId: string) {
    if (!map[activityId]) {
        Vue.set(map, activityId, {
            list: [],
            loaded: false,
            requesting: false,
            user: null,
        });
    }
}

function initAffectList(map: { [key: string]: ListWrap<Monitor.AffectItem> & ListRequesting | undefined }, key: string) {
    if (!map[key]) {
        Vue.set(map, key, {
            list: [],
            loaded: false,
            requesting: false,
        });
    }
}

const m: Module<MonitorState, RootState> = {
    namespaced: true,
    state: () => ({
        activity: {},
        /** 战队列表 */
        teamListMap: {},
        teamDataMap: {},
        teamInfoMap: {},
        /** 招募榜单 */
        recruitRank: {},
        affectStat: {},
        affectList: {},
        activityIdMapPrizeList: {},
        teamSignInMap: {},
        teamSignInDetailMap: {},
        userSignInCalendarMap: {},
        userSignInStatic: {},
        inviteNews: {},
        newsRequesing: false,
        monitorInviteCard: {},
        recommendTeamList: {
            order_team: [],
            recommend_team: [],
        },
        monitorQuestion: null,
        monitorQuestionScore: null,
        teamApply: null,
        lastTeamInfo: null,
        teamVisiableMap: {},
        teamInfo: null,
    }),
    getters: {
        /** 存在战队的activityId列表 */
        monitorActivityIds(state, getters, rootState, rootGetters) {
            return rootGetters['activity/allMonitorActivityIds'].filter((activityId: string) => {
                const teamList = state.teamListMap[activityId];
                return teamList && teamList.length > 0;
            });
        },
        /** 是否有管理的战队 */
        hasMonitorTeam(state, getters) {
            return getters.monitorActivityIds.length > 0;
        },
        hasTargetMonitorTeam(state, getters, rootState, rootGetters) {
            const activityId = rootGetters['activity/activityId'];
            const teamList = state.teamListMap[activityId];
            return teamList && teamList.length > 0;
        },
        /** 最近可管理的活动id */
        mainMonitorActivityId(state, getters, rootState) {
            const activityIds = getters.monitorActivityIds || [];
            if (activityIds.length === 0) {
                return;
            }
            for (const activityId of activityIds) {
                const basicInfo = rootState.activity.basicInfo[activityId];
                // 在周期内
                if (basicInfo && (basicInfo.startMonitorApply || basicInfo.startParticipation || basicInfo.startSignIn) && !(basicInfo.closeMonitorApply && basicInfo.closeParticipation && basicInfo.closeSignIn)) {
                    return activityId;
                }
            }
            return activityIds[0];
        },
        /** 最近可以申请成为队长 */
        mainMonitorApplyActivityId(state, getters, rootState, rootGetters) {
            const activityIds = rootGetters['activity/allMonitorActivityIds'];
            for (const activityId of activityIds) {
                const basicInfo = rootState.activity.basicInfo[activityId];
                if (basicInfo && basicInfo.startMonitorApply && !basicInfo.closeMonitorApply) {
                    return activityId;
                }
            }
            return activityIds[0];
        },
    },
    mutations: {
        /**
         * 记录活动列表
         */
        setActivity(state, { mode, list }) {
            Vue.set(state.activity, mode, list);
        },
        initActivityList(state, { key }) {
            Vue.set(state.activity, key, {
                list: [],
                loaded: false,
                req: false,
            });
        },
        concatActivityList(state, { key, list }) {
            state.activity[key].list = state.activity[key].list.concat(list);
        },
        setActivityListLoaded(state, { key, loaded }) {
            state.activity[key].loaded = loaded;
        },
        setActivityListReq(state, { key, req }) {
            state.activity[key].req = req;
        },
        /**
         * 记录战队列表
         */
        setTeamList(state, { activityId, teamList }) {
            Vue.set(state.teamListMap, activityId, teamList);
        },
        /**
         * 记录战队信息
         */
        setTeamData(state, { teamId, data }) {
            Vue.set(state.teamDataMap, teamId, data );
        },
        setTeamInfo(state, { teamId, info }) {
            Vue.set(state.teamInfoMap, teamId, info);
        },
        /**
         * 添加招募排行数据
         */
        concatRecruitRank(state, { activityId, list }) {
            initRecruitRank(state.recruitRank, activityId);
            state.recruitRank[activityId]!.list.push(...list);
        },
        /**
         * 记录招募排行是否加载完成
         */
        setRecruitRankLoaded(state, { activityId, loaded }) {
            state.recruitRank[activityId]!.loaded = loaded;
        },
        /**
         * 记录招募排行是否正在请求
         */
        setRecruitRankReq(state, { activityId, req }) {
            state.recruitRank[activityId]!.requesting = req;
        },
        /**
         * 记录招募排行用户数据
         */
        setRecruitRankUser(state, { activityId, user }) {
            state.recruitRank[activityId]!.user = user;
        },
        /**
         * 记录影响数据
         */
        setAffectStat(state, { activityId, teamId, affectStat }) {
            const key = [ activityId, teamId ].join('_');
            Vue.set(state.affectStat, key, affectStat);
        },
        /**
         * 添加影响人数
         */
        concatAffectList(state, { activityId, teamId, joined, list }) {
            const key = [ activityId, teamId, joined ].join('_');
            initAffectList(state.affectList, key);
            state.affectList[key]!.list.push(...list);
        },
        /**
         * 记录是否全部加载
         */
        setAffectListLoaded(state, { activityId, teamId, joined, loaded }) {
            const key = [ activityId, teamId, joined ].join('_');
            initAffectList(state.affectList, key);
            Vue.set(state.affectList[key]!, 'loaded', loaded);
        },
        /**
         * 记录是否正字啊请求
         */
        setAffectListReq(state, { activityId, teamId, joined, req }) {
            const key = [ activityId, teamId, joined ].join('_');
            initAffectList(state.affectList, key);
            Vue.set(state.affectList[key]!, 'requesting', req);
        },
        /**
         * 记录prizeList
         */
        setPrizeList(state, { activityId, prizeList }) {
            Vue.set(state.activityIdMapPrizeList, activityId, prizeList);
        },
        /** 记录战队某天的打卡概况 */
        setTeamSignInMap(state, { teamId, date, data }) {
            Vue.set(state.teamSignInMap[teamId]!, date, data);
        },
        setTeamSignInDetailMap(state, { teamId, date, type, data }) {
            data.forEach((item: Monitor.TeamSignInDetail, index: number) => {
                item.rank = index + 1;
            });
            Vue.set(state.teamSignInDetailMap[teamId]![date]!, type, data);
        },
        setUserSignInCalendarMap(state, { activityId, userId, month, data }) {
            const key = activityId + '_' + userId;
            Vue.set(state.userSignInStatic, key, {
                nickname: data.nickname,
                avatar: data.avatar,
                total_days: data.total_days,
                total_distance: data.total_distance,
                future_day: data.future_day,
                need_day: data.need_day,
            });
            Vue.set(state.userSignInCalendarMap[userId]!, month, data);
        },
        setNews(state, { activityId, news }: { activityId: string, news: Monitor.New[] }) {
            state.inviteNews[activityId]! = news;
        },
        setNewsRequesting(state, requesting: boolean) {
            state.newsRequesing = requesting;
        },
        setMonitorInviteCard(state, { activityId, card }: { activityId: string, card: string }) {
            Vue.set(state.monitorInviteCard, activityId, card);
        },
        setMonitorRecommendTeamList(state, data) {
            state.recommendTeamList = data;
        },
        /**
         * 记录笔试题
         */
        setMonitorQuestion(state, question) {
            state.monitorQuestion = question;
        },
        /**
         * 记录笔试题分数
         */
        setMonitorQuestionScore(state, score) {
            state.monitorQuestionScore = score;
        },
        /**
         * 记录申请信息
         */
        setTeamApply(state, teamApply) {
            state.teamApply = teamApply;
        },
        /**
         * 记录上一次的创建队伍信息
         */
        setLastTeamInfo(state, teamApply) {
            state.lastTeamInfo = teamApply;
        },
        setTeamVisiableInfo(state, { teamId, data }) {
            Vue.set(state.teamVisiableMap, teamId, data && data.show);
        },
        setTeamInfoData(state, info) {
            state.teamInfo = info
        }
    },
    actions: {
        /**
         * 活动列表
         */
        async getActivityList({ state, commit }, { mode, offset, count }) {
            const key = mode;
            let wrap = state.activity[key];
            if (!wrap) {
                commit('initActivityList', { key });
                wrap = state.activity[key];
            }
            if (wrap.req || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            if (offset < len) {
                offset = len;
                count = end - offset;
            }
            commit('setActivityListReq', { key, req: true });
            const response = await Vue.axios.get('/api/v1/monitor/activity_list', {
                params: {
                    mode,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data;
                commit('concatActivityList', { key, list });
                if (list.length < count) {
                    commit('setActivityListLoaded', { key, loaded: true });
                }
            }
            commit('setActivityListReq', { key, req: false });
            return rdata;
        },
        /**
         * 获取战队列表
         */
        async getTeamList({ state, commit }, { activityId, forceUpdate }) {
            // 数据缓存
            if (state.teamListMap[activityId] && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/monitor/team_list', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setTeamList', {
                    activityId,
                    teamList: rdata.data,
                });
            } else {
                commit('setTeamList', {
                    activityId,
                    teamList: [],
                });
            }
            return rdata;
        },
        /**
         * 获得战队信息
         */
        async getTeamData({ state, commit }, { teamId, forceUpdate }) {
            if (state.teamDataMap[teamId] && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/monitor/team_data', {
                params: {
                    id: teamId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setTeamData', {
                    teamId,
                    data: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获得战队信息
         */
        async getTeamInfo({ state, commit }, { teamId, forceUpdate }) {
            if (state.teamInfoMap[teamId]  && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/monitor/team_info', {
                params: {
                    id: teamId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setTeamInfo', {
                    teamId,
                    info: new TeamApply(rdata.data as Monitor.RawTeamApply),
                });
            }
            return rdata;
        },
        /**
         * 获得上次创建队伍信息
         */
        async getLastTeamInfo({ state, commit }) {
            const response = await Vue.axios.get('/api/v1/monitor/last_team_info');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setLastTeamInfo',  new TeamApply(rdata.data as Monitor.RawTeamApply));
            }
            return rdata;
        },
        /**
         * 更新战队信息
         */
        async updateTeamInfo({ state, commit, dispatch }, { teamId }) {
            const teamApply = state.teamInfoMap[teamId];

            const response = await Vue.axios.post('/api/v1/monitor/team_info', {
                id: teamId,
                ...teamApply?.toRaw(),
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                await dispatch('getTeamInfo', {
                    teamId,
                    forceUpdate: true,
                });
            }
            return rdata;
        },
        /**
         * 招募排行
         */
        async getRecruitRank({ state, commit }, { activityId, offset, count }) {
            initRecruitRank(state.recruitRank, activityId);
            const wrap = state.recruitRank[activityId]!;
            const end = offset + count;
            if (end <= wrap.list.length) {
                return;
            }
            if (offset < wrap.list.length) {
                offset = wrap.list.length;
                count = end - offset;
            }
            if (wrap.loaded || wrap.requesting) {
                return;
            }
            commit('setRecruitRankReq', {
                activityId,
                req: true,
            });
            const response = await Vue.axios.get('/api/v1/monitor/rank', {
                params: {
                    id: activityId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('concatRecruitRank', {
                    activityId,
                    list: rdata.data.list,
                });
                if (rdata.data.list.length < count) {
                    commit('setRecruitRankLoaded', {
                        activityId,
                        loaded: true,
                    });
                }
                if (rdata.data.current) {
                    commit('setRecruitRankUser', {
                        activityId,
                        user: rdata.data.current,
                    });
                }
            }
            commit('setRecruitRankReq', {
                activityId,
                req: false,
            });
            return rdata;
        },
        /**
         * 获取影响状态
         */
        async getAffectStat({ state, commit }, { activityId, teamId }) {
            const response = await Vue.axios.get('/api/v1/monitor/affect_stats', {
                params: {
                    activity_id: activityId,
                    team_id: teamId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setAffectStat', {
                    activityId,
                    teamId,
                    affectStat: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获取影响人员列表
         */
        async getAffectList({ state, commit }, { activityId, teamId, joined, offset, count }) {
            const key = [ activityId, teamId, joined ].join('_');
            initAffectList(state.affectList, key);
            const wrap = state.affectList[key]!;
            if (wrap.loaded || wrap.requesting) {
                return;
            }
            const end = offset + count;
            if (end <= wrap.list.length) {
                return;
            }
            if (offset < wrap.list.length) {
                offset = wrap.list.length;
                count = end - offset;
            }
            commit('setAffectListReq', {
                activityId,
                teamId,
                joined,
                req: true,
            });
            const response = await Vue.axios.get('/api/v1/monitor/affect_list', {
                params: {
                    activity_id: activityId,
                    team_id: teamId,
                    joined,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('concatAffectList', {
                    activityId,
                    teamId,
                    joined,
                    list: rdata.data,
                });
                if (rdata.data.length < count) {
                    commit('setAffectListLoaded', {
                        activityId,
                        teamId,
                        joined,
                        loaded: true,
                    });
                }
            }
            commit('setAffectListReq', {
                activityId,
                teamId,
                joined,
                req: false,
            });
            return rdata;
        },
        /**
         * 礼品列表
         */
        async getPrizeList({ state, commit }, { activityId, forceUpdate }) {
            if (state.activityIdMapPrizeList[activityId] && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/monitor/prize_list', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setPrizeList', {
                    activityId,
                    prizeList: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获取战队打卡信息概况
         */
        async getTeamSignIn({ state, commit }, { activityId, teamId, date, forceUpdate }) {
            if (!state.teamSignInMap[teamId]) {
                Vue.set(state.teamSignInMap, teamId, {});
            }
            if (state.teamSignInMap[teamId]![date] && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/monitor/team_sign_in', {
                params: {
                    activity_id: activityId,
                    team_id: teamId,
                    date,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setTeamSignInMap', {
                    teamId,
                    date,
                    data: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获取战队打卡详情
         * @param teamId 战队id
         * @param date 日期 格式 'Y-m-d'
         * @param type all yes no  全部 打卡成功 审核未通过或者未打卡
         */
        async getTeamSignInDetail({ state, commit }, { teamId, date, type, forceUpdate }) {
            if (!state.teamSignInDetailMap[teamId]) {
                Vue.set(state.teamSignInDetailMap, teamId, {});
            }
            if (!state.teamSignInDetailMap[teamId]![date]) {
                Vue.set(state.teamSignInDetailMap[teamId]!, date , {});
            }
            if (state.teamSignInDetailMap[teamId]![date]![type] && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/monitor/team_sign_in_detail', {
                params: {
                    team_id: teamId,
                    date,
                    type,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setTeamSignInDetailMap', {
                    teamId,
                    date,
                    type,
                    data: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 战队中某个队员打卡日历信息
         * @param activityId 活动id
         * @param userId 用户Id
         * @param month 日期 格式 'Y-m'
         */
        async getUserSignInCalendar({ state, commit }, { activityId, teamId, userId, month, forceUpdate }) {
            if (!state.userSignInCalendarMap[userId]) {
                Vue.set(state.userSignInCalendarMap, userId, {});
            }
            if (state.userSignInCalendarMap[userId]![month] && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/monitor/team_sign_in_calendar', {
                params: {
                    activity_id: activityId,
                    team_id: teamId,
                    user_id: userId,
                    month,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setUserSignInCalendarMap', {
                    activityId,
                    userId,
                    month,
                    data: rdata.data,
                });
            }
            return rdata;
        },
         /**
          * 最新战报
          * @param activityId
          * @param count
          * @param lastId
          * @param forceUpdate
          */
         async getNews({ state, commit }, { activityId }: { activityId: string }) {
            if (isUndef(state.inviteNews[activityId])) {
                Vue.set(state.inviteNews, activityId, []);
            }
            if (state.newsRequesing) {
                return;
            }
            commit('setNewsRequesting', true);
            const response = await Vue.axios.get('/api/v1/monitor/monitor_invite_count', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            commit('setNewsRequesting', false);
            if (rdata.status === 0) {
                commit('setNews', {
                    activityId,
                    news: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获得战队邀请卡
         *
         * @param {string} activityId
         */
        async getInviteCard({ state, commit }, { activityId, forceUpdate }: { activityId: string, forceUpdate?: boolean }) {
            const temp = state.monitorInviteCard[activityId];
            if (temp && !forceUpdate) {
                return {
                    status: 0,
                    data: temp,
                };
            }
            const response = await Vue.axios.get('/api/v1/monitor/monitor_invite_card', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setMonitorInviteCard', {
                    activityId,
                    card: rdata.data,
                });
            }
            return rdata;
        },

        /**
         * 获取推荐战队
         * @param {string} activityId 必填
         * @param {string} teamId 战队id 可选
         * @param {string} distance 期待战队的所在组别 可选
         * @param {number} cityId 期待战队所在城市 可选
         * @param {number} activitySpecialId 主题活动id 可选
         */
        async getRecommendTeams({ state, commit }, { activityId, teamId, distance, cityId, activitySpecialId }: { activityId: string, teamId?: string, distance?: string, cityId?: number, activitySpecialId?: number }) {
            const response = await Vue.axios.get('/api/v1/monitor/recommend_team_list', {
                params: {
                    activity_id: activityId,
                    team_id: teamId,
                    distance,
                    city_id: cityId ? cityId : undefined,
                    activity_special_id: activitySpecialId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setMonitorRecommendTeamList', rdata.data);
            }
        },
        /**
         * 获取笔试题
         */
        async getMonitorQuestion(context) {
            const response = await Vue.axios.get('/api/v1/activity/monitor_question');
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setMonitorQuestion', rdata.data);
            }
            return rdata;
        },
        /**
         * 提交答案
         */
        async answerMonitorQuestion(context, result: { [key: number]: number[] }) {
            const answer: Array<{ question: string; answer: number[] }> = [];
            for (const key in result) {
                if (result[key]) {
                    answer.push({
                        question: key,
                        answer: result[key],
                    });
                }
            }
            const response = await Vue.axios.post('/api/v1/activity/answer_monitor_question', {
                answer,
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setMonitorQuestionScore', rdata.data);
            }
            return rdata;
        },
        /**
         * 获取申请队伍信息
         */
        async getApplyTeam(context) {
            const response = await Vue.axios.get('/api/v1/monitor_apply/team_info');
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setTeamApply', new TeamApply(rdata.data as Monitor.RawTeamApply));
            }
            return rdata;
        },
        /**
         * 提交申请队伍信息
         */
        async applyTeam(context) {
            const apply = context.state.teamApply!;
            const response = await Vue.axios.post('/api/v1/monitor_apply/team_info', {
                ...apply.toRaw(),
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 获取队伍是否在首页展示状态
         */
        async getTeamVisiableInfo(context, { teamId, forceUpdate }: { teamId: number, forceUpdate?: boolean }) {
            if (context.state.teamVisiableMap[teamId] && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/team/info', {
                params: {
                    id: teamId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setTeamVisiableInfo', {
                    teamId,
                    data: rdata.data,
                });
                context.commit('setTeamInfoData', rdata.data);
            }
            return rdata;
        },
        /**
         * 更新队伍展示状态
         */
        async updateTeamVisiableInfo(context, { teamId, show }: { teamId: number, show: boolean }) {
            const response = await Vue.axios.post('/api/v1/team/show', {
                id: teamId,
                show: show ? 1 : 0,
            });
            const rdata = response.data;
            return rdata;
        },
    },
};

export default m;
